import { styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Error from '@/components/error/Error';
import UnauthenticatedLayout from '@/components/layout/UnauthenticatedLayout';
import { FF_ENABLE_ESCRIPT_ORDERS } from '@/constants/featureFlags';
import useClickAndCollectLocationQuery from '@/hooks/order/useClickAndCollectLocationQuery';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import useCustomer from '@/hooks/user/useCustomer';
import {
  ESCRIPT_PAYMENT_SUCCESS_ROUTE,
  LOGIN_ROUTE,
  PATIENT_CLICK_AND_COLLECT,
  PATIENT_ORDERS_ROUTE,
  PATIENT_PAYMENT_SUCCESS_ROUTE,
  PATIENT_REFILL_PHARMACY_ROUTE,
  PATIENT_REFILL_ROUTE,
  PAYMENT_CONFIRM_ROUTE,
  PAYMENT_SUCCESS_ROUTE,
  PROFILE_DETAILS_ROUTE,
} from '@/routes/route-paths';
import { Logger } from '@/utils/logger';
import { useSession } from '@montugroup/circuit-shared';
import { PaymentService } from '@montugroup/circuit-shared/services/payment.service';
import { CircularProgress, Container } from '@montugroup/design-system';

const logger = new Logger('PaymentConfirm');

const ContentContainer = styled(Container)`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

function PaymentConfirm() {
  const [error, setError] = useState<boolean>(false);

  const navigate = useNavigate();
  const { state, search } = useLocation();

  const orderCode = state?.from?.state?.orderCode || PaymentService.getOrderCode();
  const usedDeliveryOption = state?.from?.state?.usedDeliveryOption || PaymentService.getUsedDeliveryOption();
  const invoiceToken = PaymentService.getInvoiceToken();

  // Cant use useParams() here as the link from Shopify will end with /payment-confirm/?<our params>
  const params = new URLSearchParams(search);
  const redirect = params.get('redirectTo');
  const { flags } = useFeatureFlags();
  const { status } = useSession();
  const { data: customer } = useCustomer();
  const { clearLocationQuery } = useClickAndCollectLocationQuery();
  const isLoggedIn = status === 'authenticated';
  const isEscriptCustomer = isLoggedIn && customer?.isExternal && flags[FF_ENABLE_ESCRIPT_ORDERS];

  /**
   * UI Extensions that want to take the patient to a particular page direct to this page with the redirectTo param.
   *
   * Current redirects in place:
   *   redirectTo=refill -> /patient/refill ('Back to refill' button)
   *   redirectTo=profile -> /profile-detail (Address validation extension)
   */
  const handleRedirect = () => {
    let path;
    switch (redirect) {
      case 'refill':
        path = PATIENT_REFILL_ROUTE;
        break;
      case 'home':
        path = '/';
        break;
      case 'shipping':
        path = PATIENT_REFILL_PHARMACY_ROUTE;
        break;
      case 'collection_location':
        path = PATIENT_CLICK_AND_COLLECT;
        break;
      default:
        path = PROFILE_DETAILS_ROUTE;
    }

    if (!isLoggedIn) {
      navigate(LOGIN_ROUTE, { state: { from: { pathname: path } } });
    } else {
      navigate(path);
    }
  };

  /**
   * Returning to circuit from a non-invoice flow
   */
  const handleRefill = async () => {
    if (!isLoggedIn) {
      // Unexpected for refill flow - push to redirect and then back here
      navigate(LOGIN_ROUTE, {
        state: {
          orderCode,
          usedDeliveryOption,
          from: { pathname: PAYMENT_CONFIRM_ROUTE },
        },
      });
    } else if (!orderCode) {
      logger.error('orderCode not found');
      setError(true);
    } else {
      const { status, isPaid, error: responseError } = await PaymentService.checkPaymentStatus(orderCode);

      if (isPaid) {
        PaymentService.clearOrderCode();
        PaymentService.clearUsedDeliveryOption();
        clearLocationQuery();
        navigate(isEscriptCustomer ? ESCRIPT_PAYMENT_SUCCESS_ROUTE : PATIENT_PAYMENT_SUCCESS_ROUTE, {
          state: { orderCode, usedDeliveryOption },
        });
      } else {
        if (responseError) {
          logger.error('Error checking payment status', status, responseError);
        } else {
          logger.error('Unknown error checking payment status');
        }
        setError(true);
      }
    }
  };

  const handleInvoice = async () => {
    if (!invoiceToken || !orderCode) {
      logger.error(`${!invoiceToken ? 'invoiceToken' : 'orderCode'} not found`);
      setError(true);
    } else {
      const {
        status,
        isPaid,
        user,
        onBoardKey,
        error: responseError,
      } = await PaymentService.checkPaymentStatusForInvoice(invoiceToken, orderCode);
      if (isPaid) {
        PaymentService.clearInvoiceToken();
        PaymentService.clearOrderCode();
        PaymentService.clearUsedDeliveryOption();
        const { firstName, email } = user;
        navigate(PAYMENT_SUCCESS_ROUTE, {
          state: {
            firstName,
            email,
            onBoardKey,
            orderCode,
            usedDeliveryOption,
          },
        });
      } else {
        if (responseError) {
          logger.error('Error checking invoice payment status', status, responseError);
        } else {
          logger.error('Unknown error checking invoice payment status');
        }
        setError(true);
      }
    }
  };

  /**
   * Fallback to direct patient somewhere when expected conditions aren't met
   */
  const handleUnexpected = () => {
    logger.warn('Page loaded without orderCode.');
    if (isLoggedIn) {
      navigate(PATIENT_ORDERS_ROUTE);
    } else {
      navigate(LOGIN_ROUTE, { state: { from: { pathname: PATIENT_ORDERS_ROUTE } } });
    }
  };

  useEffect(() => {
    if (redirect) {
      handleRedirect();
    } else if (orderCode && invoiceToken) {
      handleInvoice();
    } else if (orderCode) {
      handleRefill();
    } else {
      handleUnexpected();
    }
  }, []);

  return (
    <UnauthenticatedLayout>
      {!error && <CircularProgress />}
      {error && (
        <ContentContainer>
          <Error />
        </ContentContainer>
      )}
    </UnauthenticatedLayout>
  );
}

export default PaymentConfirm;
